import Route from '@ember/routing/route';
import type RouterService from '@ember/routing/router-service';
import type Transition from '@ember/routing/transition';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type Store from '@ember-data/store';
import type AbilitiesService from 'ember-can/services/abilities';
import type AreaMapModel from 'garaje/models/area-map';
import type DeskModel from 'garaje/models/desk';
import type MapFeatureModel from 'garaje/models/map-feature';
import type MapFloorModel from 'garaje/models/map-floor';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type ApolloService from 'garaje/services/apollo-extension';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LiveMapService from 'garaje/services/live-map';
import type MapVersionsService from 'garaje/services/map-versions';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { routeEvent } from 'garaje/utils/decorators/route';

import type MapsLiveShowController from './controller';

interface ModelParams {
  mapFeatures: MapFeatureModel[];
}

export interface ModelResponse {
  areaMap: AreaMapModel;
  mapFloor: MapFloorModel | null;
  mapFloors: MapFloorModel[];
  mapFeatures: MapFeatureModel[];
  desksInLocation: DeskModel[];
  workplaceDays: WorkplaceDayModel[];
}

interface AfterModelParams {
  mapFloors: MapFloorModel[];
  mapFloor: MapFloorModel;
}

export default class MapsLiveShowRoute extends Route {
  @service declare store: Store;
  @service declare state: StateService;
  @service declare metrics: MetricsService;
  @service declare router: RouterService;
  @service declare abilities: AbilitiesService;
  @service declare apollo: ApolloService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare flashMessages: FlashMessagesService;
  @service declare mapVersions: MapVersionsService;
  @service declare liveMap: LiveMapService;

  queryParams = {
    selectedTime: {
      refreshModel: false,
    },
    selectedResourceFilter: {
      refreshModel: false,
    },
    selectedEmployeeId: {
      refreshModel: false,
    },
    selectedFeatureId: {
      refreshModel: false,
    },
    openProfileModal: {
      refreshModel: false,
    },
  };

  async beforeModel(): Promise<void> {
    if (this.abilities.cannot('visit-live-maps maps')) {
      return void this.router.transitionTo('unauthorized');
    }

    // We need to query for map floors to check if there is an image
    const mapFloors = await this.store.query('map-floor', { filter: { location: this.state.currentLocation?.id } });
    const hasMapFloorWithRasterImage = isPresent(
      mapFloors.filter((floor) => {
        return floor.rasterImageUrl;
      }),
    );

    if (!hasMapFloorWithRasterImage && this.abilities.cannot('visit-live-maps maps')) {
      return void this.router.transitionTo('unauthorized');
    }

    return void this.metrics.trackEvent('Accessed live map');
  }

  async fetchWorkplaceDays(): Promise<WorkplaceDayModel[]> {
    let workplaceDays: WorkplaceDayModel[] = [];
    if (this.state.currentLocationEnabledDesks?.id) {
      await this.store.findRecord('desk-location', this.state.currentLocationEnabledDesks.id, {
        reload: true,
        include: 'workplace-days',
      });

      workplaceDays = this.store.peekAll('workplaceDay').filter((day: WorkplaceDayModel) => {
        return day.belongsTo('deskLocation').id() === this.state.currentLocationEnabledDesks?.id;
      }) as WorkplaceDayModel[];
    }
    return workplaceDays;
  }

  async model({ floor_id: floorId }: { floor_id: string }, transition: Transition): Promise<ModelResponse> {
    const { areaMap } = this.modelFor('spaces.maps') as { areaMap: AreaMapModel };

    const selectedTimeFromTransition = transition?.to?.queryParams?.['selectedTime'];
    const selectedTimeFromRouter = this.router.currentRoute?.queryParams?.['selectedTime'];
    const selectedTime = selectedTimeFromTransition ?? selectedTimeFromRouter;
    const liveMapFilters = selectedTime ? { selectedTime: Number(selectedTime), floorId } : { floorId };

    await this.liveMap.getUpdatedLiveMapData(liveMapFilters);

    const mapFloors = this.store
      .peekAll('map-floor')
      .filter((mapFloor) => mapFloor.belongsTo('areaMap').id() === areaMap.id);
    const mapFloor = mapFloors.find((floor) => floor.id === floorId) as MapFloorModel;
    // Extract map features once we've loaded the store with the snapshot data
    const mapFeatures = this.store.peekAll('map-feature').slice();
    const mapFeaturesForFloor = mapFeatures.filter((mapFeature) => mapFeature.belongsTo('mapFloor').id() === floorId);

    // Extract desks
    const desks = this.store.peekAll('desk').slice();

    const workplaceDays = await this.fetchWorkplaceDays();

    return {
      areaMap,
      mapFloors,
      mapFloor,
      mapFeatures: mapFeaturesForFloor,
      desksInLocation: desks,
      workplaceDays,
    };
  }

  afterModel({ mapFloors, mapFloor }: AfterModelParams): void {
    const hasFloorInLocation = mapFloors.some((floor: MapFloorModel) => floor.id === mapFloor?.id);

    if (!hasFloorInLocation) {
      void this.router.transitionTo('spaces.maps.live.show', mapFloors[0]?.id || '');
    }

    if (!mapFloor.rasterImageUrl) {
      const floorWithUrl = mapFloors.find((floor) => floor.rasterImageUrl);

      if (floorWithUrl) {
        return void this.router.transitionTo('spaces.maps.live.show', floorWithUrl.id);
      }

      return void this.router.transitionTo('spaces.maps');
    }
  }

  setupController(controller: MapsLiveShowController, model: ModelParams, transition: Transition): void {
    super.setupController(controller, model, transition);
    controller.mapFeatures = model.mapFeatures;
    void controller.loadResourcesDataTask.perform();
    controller.setSelectedDesk();

    // Handle group desk reservation success flash message
    if (controller.groupDeskReservationCount) {
      const count = controller.groupDeskReservationCount;
      this.flashMessages.showAndHideFlash('success', 'Desks reserved', {
        details: `${count} ${count === 1 ? 'desk' : 'desks'} reserved for your group`,
      });
      // Clear the query param after showing the message
      controller.groupDeskReservationCount = null;
      void this.router.transitionTo({ queryParams: { groupDeskReservationCount: null } });
    }

    if (this.featureFlags.isEnabled('shared-desks-scheduling')) {
      controller.initializeSharedDesksState();
    }

    // Handle email deep-link: auto-open profile card when ?openProfileModal=true
    // is present along with selectedEmployeeId.
    controller.maybeOpenProfileCardFromQueryParam();
  }

  @routeEvent
  routeWillChange(): void {
    this.liveMap.willDestroy();
  }
}
